import { onLoaded, updateContent, emitEvent } from "@/utils"

/**
 * Handles AJAX-powered comment form submission for WordPress.
 * Attaches a delegated event listener to the document to capture submit events
 * on #commentform, ensuring compatibility with dynamically loaded content.
 */

// Prevent multiple initializations
let commentsInitialized = false

const Comments = () => {
    // Prevent re-initialization
    if (commentsInitialized) {
        return
    }

    /**
     * Handles the comment form submission event.
     *
     * @param {Event} e The submit event object.
     */
    const handle_form_submission = async function( e ) {
        // Only handle if the event target matches #commentform
        if ( ! e.target || ! e.target.matches || ! e.target.matches( 'form#commentform' ) ) {
            return;
        }

        e.preventDefault();

        var form = e.target;
        var form_data = new window.FormData( form );
        var action = form.getAttribute( 'action' );

        try {
            form.classList.add( 'ajaxpress-comment-form-processing' );

            // Submit form to wp-comments-post.php
            var response = await window.fetch( action, {
                method: 'POST',
                body: form_data
            } );
            var response_data = await response.text();

            form.classList.remove( 'ajaxpress-comment-form-processing' );

            // Update the content (pass flag to skip scroll_to_top).
            await updateContent( response_data, 'comment' );

            // Emit updated event for animations (progressbar, loader, etc.)
            emitEvent('ajaxpress-updated', {
                html: response_data,
                source: 'comment'
            });

            // WordPress typically redirects to the new comment with hash like #comment-123
            // Extract comment ID from response URL if present
            let commentId = null;

            // Check if response has a Location header with comment hash
            const locationHeader = response.headers.get('Location');
            if (locationHeader && locationHeader.includes('#comment-')) {
                const match = locationHeader.match(/#comment-(\d+)/);
                if (match) {
                    commentId = match[1];
                }
            }

            // If no comment ID from header, try to find the newest comment by looking for moderation message or latest comment
            if (!commentId) {
                // Check for comment awaiting moderation
                const moderationNotice = document.querySelector('.comment-awaiting-moderation');
                if (moderationNotice) {
                    const commentElement = moderationNotice.closest('.comment, li[id^="comment-"]');
                    if (commentElement && commentElement.id) {
                        commentId = commentElement.id.replace('comment-', '');
                    }
                }
            }

            // Update the URL in browser history with comment hash
            let newUrl = window.location.href.split('#')[0];
            if (commentId) {
                newUrl += '#comment-' + commentId;
            }

            if ( window.history && window.history.pushState ) {
                window.history.pushState(
                    {
                        url: newUrl,
                        source: 'ajaxpress'
                    },
                    '',
                    newUrl
                );
            }

            // Scroll to the new comment
            let targetComment = null;
            if (commentId) {
                targetComment = document.getElementById('comment-' + commentId);
            }

            // Fallback to last comment if we couldn't find the specific one
            if (!targetComment) {
                targetComment = document.querySelector('.comment:last-of-type, li[id^="comment-"]:last-of-type');
            }

            if ( targetComment ) {
                // Add delay to ensure content and scripts are fully rendered
                // Wait longer than updateContent's script initialization (100ms)
                setTimeout(() => {
                    targetComment.scrollIntoView( { behavior: 'smooth', block: 'center' } );
                }, 200);
            }
        } catch ( err ) {
            // Optionally, handle error (e.g., show error message)
            // console.error( 'AjaxPress: Comment submission failed.', err );
        }
    };

    /**
     * Initializes the comment form handler by attaching a delegated event listener.
     * Uses event delegation to ensure dynamically loaded forms are handled.
     */
    const init = function() {
        // Prevent re-initialization
        if (commentsInitialized) {
            return
        }

        // Mark as initialized
        commentsInitialized = true

        // Use event delegation to handle dynamically replaced comment forms.
        document.addEventListener( 'submit', handle_form_submission, false );
    };

    onLoaded( init );
};

export default Comments